import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const statusBadgeVariants = cva(
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors',
  {
    variants: {
      tone: {
        success:
          'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-200',
        warning:
          'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-200',
        error:
          'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-200',
        info: 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-200',
        neutral: 'bg-muted text-muted-foreground',
      },
      bordered: {
        true: 'border',
        false: 'border-transparent',
      },
    },
    compoundVariants: [
      {
        tone: 'success',
        bordered: true,
        className:
          'border-success-300 dark:border-success-700',
      },
      {
        tone: 'warning',
        bordered: true,
        className:
          'border-warning-300 dark:border-warning-700',
      },
      {
        tone: 'error',
        bordered: true,
        className:
          'border-error-300 dark:border-error-700',
      },
      {
        tone: 'info',
        bordered: true,
        className:
          'border-info-300 dark:border-info-700',
      },
      {
        tone: 'neutral',
        bordered: true,
        className: 'border-border',
      },
    ],
    defaultVariants: {
      tone: 'neutral',
      bordered: false,
    },
  },
);

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {}

const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ className, tone, bordered, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(statusBadgeVariants({ tone, bordered, className }))}
      {...props}
    />
  ),
);
StatusBadge.displayName = 'StatusBadge';

export { StatusBadge, statusBadgeVariants };
